import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import { buttonVariants } from "@/components/ui";
import { LoadingState, SectionCard } from "@/components/common";
import { toastApiError } from "@/lib/errorMessages";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import {
  adminNotificationService,
  type AdminNotification,
} from "@/services/adminNotificationService";
import {
  adminNotificationKeys,
  useAdminNotificationStatusCounts,
} from "@/hooks/useAdminNotificationStatusCounts";
import { NotificationCard } from "@/pages/admin/notifications/NotificationCard";
import { NOTIFICATIONS_DASHBOARD_PREVIEW_SIZE } from "@/pages/admin/notifications/constants";

export function AdminDashboardNotifications() {
  const { t } = useTranslation(["dashboard", "adminNotifications"]);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const [actionId, setActionId] = useState<string | null>(null);

  const canList = hasPermission(Permission.NOTIFICATIONS_READ);
  const canUpdate = hasPermission(Permission.NOTIFICATIONS_UPDATE);

  const previewQueryKey = [
    ...adminNotificationKeys.all,
    "dashboard",
    "open",
  ] as const;
  const { data, isLoading, error } = useQuery({
    queryKey: previewQueryKey,
    queryFn: () =>
      adminNotificationService.list({
        status: "open",
        limit: NOTIFICATIONS_DASHBOARD_PREVIEW_SIZE,
      }),
    enabled: canList,
  });
  const { data: statusCounts } = useAdminNotificationStatusCounts({}, canList);

  if (!canList) {
    return null;
  }

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: adminNotificationKeys.all,
    });
  };

  const runAction = async (
    id: string,
    action: "acknowledge" | "resolve" | "reopen",
  ) => {
    setActionId(id);
    try {
      const updated =
        action === "acknowledge"
          ? await adminNotificationService.acknowledge(id)
          : action === "resolve"
            ? await adminNotificationService.resolve(id)
            : await adminNotificationService.reopen(id);

      queryClient.setQueryData(
        previewQueryKey,
        (prev: { total: number; items: AdminNotification[] } | undefined) => {
          if (!prev) {
            return prev;
          }
          if (updated.status !== "open") {
            return {
              total: Math.max(0, prev.total - 1),
              items: prev.items.filter((row) => row.id !== updated.id),
            };
          }
          return {
            ...prev,
            items: prev.items.map((row) =>
              row.id === updated.id ? updated : row,
            ),
          };
        },
      );
      invalidate();
      toast.success(t(`adminNotifications:actions.${action}Success`));
    } catch (err) {
      toastApiError(
        err,
        undefined,
        t(`adminNotifications:actions.${action}Error`),
      );
    } finally {
      setActionId(null);
    }
  };

  const items = data?.items ?? [];
  const openCount = statusCounts?.open ?? 0;

  return (
    <SectionCard
      title={t("dashboard:admin.notificationsTitle")}
      icon={Bell}
      count={openCount}
      countClassName="bg-error-50 text-error-700"
      action={
        <Link
          to="/admin/notifications?status=open"
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          {t("dashboard:admin.notificationsViewAll")}
          <ChevronRight
            className="ml-1 h-4 w-4"
            aria-hidden
          />
        </Link>
      }
    >
      {isLoading ? (
        <LoadingState message={t("dashboard:admin.notificationsLoading")} />
      ) : error ? (
        <p className="text-surface-500 text-sm">
          {t("dashboard:admin.notificationsError")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-surface-500 text-sm">
          {t("dashboard:admin.notificationsEmpty")}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              isBusy={actionId === notification.id}
              showActions={canUpdate}
              onAcknowledge={(id) => void runAction(id, "acknowledge")}
              onResolve={(id) => void runAction(id, "resolve")}
              onReopen={(id) => void runAction(id, "reopen")}
            />
          ))}
          {openCount > items.length && (
            <p className="text-surface-500 pt-1 text-center text-[13px]">
              {t("dashboard:admin.notificationsMore", {
                count: openCount - items.length,
              })}
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}
